import React, { useEffect, useState } from "react";
import { getAllVotes, getUserBallots } from "../../../services/voteServices";
import { toast } from "react-toastify";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import Button from "./../../../components/Button/Button";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ParticipateVoteModal from "./ParticipateVoteModal";
import "react-toastify/dist/ReactToastify.css";

const UserVotePage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;
  const [votes, setVotes] = useState([]);
  const [ballots, setBallots] = useState([]);
  const navigate = useNavigate();
  const [selectedVoteId, setSelectedVoteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    fetchVotesAndBallots();
  }, []);

  const fetchVotesAndBallots = async () => {
    try {
      const [voteData, ballotData] = await Promise.all([
        getAllVotes(),
        getUserBallots(userId),
      ]);

      const today = dayjs();
      const activeVotes = voteData.filter((vote) => {
        const start = dayjs(vote.start_time);
        const end = dayjs(vote.end_time);
        return (
          vote.is_active &&
          today.isAfter(start.subtract(1, "day")) &&
          today.isBefore(end.add(1, "day"))
        );
      });

      setVotes(activeVotes);
      setBallots(ballotData);
    } catch (error) {
      console.error("Erreur chargement votes ou réponses:", error);
      toast.error("Erreur lors du chargement des votes ou réponses");
    }
  };

  const handleParticipate = (voteId) => {
    setSelectedVoteId(voteId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedVoteId(null);
    setOpenModal(false);
  };

  const hasVoted = (voteId) => {
    return ballots.find((b) => b.vote_id === voteId);
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <h2 className="text-2xl font-light tracking-[0.3em] text-center mb-2">
        UTILISATEURS
      </h2>
      <h2 className="text-3xl font-bold uppercase tracking-widest text-center mb-4 font-montserrat">
        VOTES & SONDAGES
      </h2>
      <DashboardNavigation role={userRole} />

      <div className="flex flex-col items-center w-full px-4 mt-4 max-w-[1280px] mx-auto">
        {votes.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-300">
            Aucun vote ou sondage actif actuellement.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {votes.map((vote) => {
              const ballot = hasVoted(vote.id);
              return (
                <div
                  key={vote.id}
                  className={`relative bg-white dark:bg-slate-300 shadow rounded-xl p-5 flex flex-col justify-between border border-gray-200 ${
                    ballot ? "opacity-70 bg-gray-100" : ""
                  }`}
                >
                  {/* Badge "Répondu le ..." */}
                  {ballot && (
                    <div className="absolute top-2 right-2 bg-black text-white text-xs px-2 py-1 rounded shadow z-10">
                      Répondu le {dayjs(ballot.timestamp).format("DD/MM/YYYY")}
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-bold mb-1 text-primary">
                      {vote.title}
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-800 italic mb-2">
                      {vote.description}
                    </p>

                    <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded mb-3">
                      Type : {vote.type === "sondage" ? "Sondage" : "Vote"}
                    </span>

                    <div className="text-sm text-gray-600 dark:text-gray-700 mb-2">
                      <strong>Période :</strong>{" "}
                      {dayjs(vote.start_time).format("DD/MM/YYYY")} →{" "}
                      {dayjs(vote.end_time).format("DD/MM/YYYY")}
                    </div>
                  </div>

                  {!ballot && (
                    <Button
                      text="Répondre"
                      onClick={() => handleParticipate(vote.id)}
                      className="mt-2"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openModal && selectedVoteId && (
        <ParticipateVoteModal
          isOpen={openModal}
          voteId={selectedVoteId}
          userId={userId}
          onClose={handleCloseModal}
          refreshVotes={fetchVotesAndBallots} // ✅ ici
        />
      )}
    </div>
  );
};

export default UserVotePage;
